import { IconButton } from '@atlaskit/button/new';
import { FlagGroup } from '@atlaskit/flag';
import Heading from '@atlaskit/heading';
import CloseIcon from '@atlaskit/icon/core/cross';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { type ReactNode, useEffect } from 'react';
import { FormattedMessage } from 'react-intl-next';

const flagStyles = {
	boxShadow: 'elevation.shadow.overlay',
	borderRadius: 'radius.small',
	zIndex: 'flag',
	transition: 'background-color 200ms',
	backgroundColor: 'elevation.surface.overlay',
	paddingBlock: 'space.250',
	paddingRight: 'space.150',
	color: 'color.text.subtle',
} as const;

const feedbackIconStyles = xcss({
	paddingInline: 'space.200',
});

const surveyLinkStyles = xcss({
	fontWeight: 'font.weight.medium',
});

type Props = {
	width: number;
	header: ReactNode;
	description: ReactNode;
	onDismissedPermanently: () => void;
	onPrimaryCTAClicked: () => void;
};

const CloseButton = ({ onClick }: { onClick: () => void }) => {
	return (
		<IconButton
			appearance="subtle"
			icon={(iconProps) => <CloseIcon {...iconProps} />}
			label="Close Flag"
			onClick={onClick}
			spacing="compact"
		/>
	);
};

const SurveyLink = ({ onClick }: { onClick: () => void }) => {
	return (
		<Box xcss={surveyLinkStyles}>
			<a
				href="https://www.userinterviews.com/projects/-eEm9Lz9KQ/apply"
				rel="noopener noreferrer"
				target="_blank"
				onClick={onClick}
			>
				<FormattedMessage
					id="post-office.post-office-team.jira-messaging-research-recruitment.screen-space-flags.cta-link"
					description="Link for Jira Messaging Research Recruitment message in Screen Space Flags"
					defaultMessage="Get started"
				/>
			</a>
		</Box>
	);
};

export const JiraMessagingResearchRecruitmentFlag = ({
	width,
	header,
	description,
	onDismissedPermanently,
	onPrimaryCTAClicked,
}: Props) => {
	const { messageViewed } = useMessageEvent();
	const { messageInstanceViewed } = useMessageInstanceEvent();

	useEffect(() => {
		if (!messageViewed || !messageInstanceViewed) {
			return;
		}

		void messageViewed();
		void messageInstanceViewed();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<FlagGroup>
			<Box xcss={xcss({ height: '100%', display: 'flex', alignItems: 'flex-end' })}>
				<Flex
					xcss={xcss({
						...flagStyles,
						width: `${width}px`,
						minWidth: `${width}px`,
						maxWidth: `${width}px`,
					})}
				>
					<Box xcss={feedbackIconStyles}>
						<FeedbackIcon label="feedback-icon" spacing="spacious" />
					</Box>
					<Flex direction="column" gap="space.100">
						<Heading size="xsmall">{header}</Heading>
						<Box>{description}</Box>
						<SurveyLink onClick={onPrimaryCTAClicked} />
					</Flex>
					<CloseButton onClick={onDismissedPermanently} />
				</Flex>
			</Box>
		</FlagGroup>
	);
};
